import React, { useMemo, useState } from 'react';
import { BellRing, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { SOUND_PRESETS, PLUS_SOUND_PRESETS, playPresetSound, DEFAULT_SOUND_ID, getSoundPresetDescription } from '../../utils/soundPresets';
import SettingsToggle from './SettingsToggle';

const NotificationSettingsSection = () => {
  const { t } = useTranslation('settings');
  const { preferences, updatePreferences, setBreakNotificationSoundId, setCalendarNotificationSoundId } = useSettings();
  const { isPlusUser } = useAuth();
  const [playingSoundId, setPlayingSoundId] = useState(null);

  const defaultSoundId = useMemo(() => preferences.notificationSoundId || DEFAULT_SOUND_ID, [preferences.notificationSoundId]);
  const selectedBreakSoundId = useMemo(
    () => preferences.breakNotificationSoundId || defaultSoundId,
    [preferences.breakNotificationSoundId, defaultSoundId]
  );
  const selectedCalendarSoundId = useMemo(
    () => preferences.calendarNotificationSoundId || defaultSoundId,
    [preferences.calendarNotificationSoundId, defaultSoundId]
  );

  const handleBreakSoundChange = (soundId) => setBreakNotificationSoundId(soundId);
  const handleCalendarSoundChange = (soundId) => setCalendarNotificationSoundId(soundId);

  const handlePreview = async (soundId) => {
    setPlayingSoundId(soundId);
    try {
      await playPresetSound(soundId);
    } catch (e) {
      console.error('Ses önizlemesi çalınamadı:', e);
    } finally {
      setPlayingSoundId((c) => (c === soundId ? null : c));
    }
  };

  const FREE_SOUND_LIMIT = 2;
  const allPresets = [...SOUND_PRESETS, ...PLUS_SOUND_PRESETS];
  const soundDisabled = preferences.playNotificationSound === false;

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-8">
        {t('notificationSection.title')}
      </h1>

      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider mb-4 mt-8">
        {t('notificationSection.notifications')}
      </h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-200">{t('notificationSection.showBreakLabel')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('notificationSection.showBreakDescription')}</p>
          </div>
          <SettingsToggle
            checked={preferences.showBreakNotifications !== false}
            onChange={() => updatePreferences({ showBreakNotifications: !(preferences.showBreakNotifications !== false) })}
            aria-label={t('notificationSection.showBreakLabel')}
          />
        </div>
        <div className="flex items-center justify-between p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-200">{t('notificationSection.showCalendarLabel')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('notificationSection.showCalendarDescription')}</p>
          </div>
          <SettingsToggle
            checked={preferences.showCalendarNotifications !== false}
            onChange={() => updatePreferences({ showCalendarNotifications: !(preferences.showCalendarNotifications !== false) })}
            aria-label={t('notificationSection.showCalendarLabel')}
          />
        </div>
        <div className="flex items-center justify-between p-4 bg-white dark:bg-[var(--color-bg-base)] border-b-0 last:border-0">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-200">{t('notificationSection.playSoundLabel')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('notificationSection.playSoundDescription')}</p>
          </div>
          <SettingsToggle
            checked={preferences.playNotificationSound !== false}
            onChange={() => updatePreferences({ playNotificationSound: !(preferences.playNotificationSound !== false) })}
            aria-label={t('notificationSection.playSoundLabel')}
          />
        </div>
      </div>

      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider mb-4 mt-8">
        {t('notificationSection.notificationSound')}
      </h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {allPresets.map((preset, index) => {
          const freeIndex = SOUND_PRESETS.indexOf(preset);
          const isLocked = !isPlusUser && (preset.isPlus || freeIndex === -1 || freeIndex >= FREE_SOUND_LIMIT);
          const isCalendarSelected = selectedCalendarSoundId === preset.id;
          const isBreakSelected = selectedBreakSoundId === preset.id;
          const isPlaying = playingSoundId === preset.id;
          const isLast = index === allPresets.length - 1;
          return (
            <div
              key={preset.id}
              className={`flex items-center justify-between p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50 ${isLast ? 'border-b-0' : ''} ${isLocked ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isLocked && <Crown className="w-4 h-4 text-amber-500 dark:text-amber-400 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 dark:text-slate-200 truncate">{preset.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{getSoundPresetDescription(preset.id)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  type="button"
                  onClick={() => handlePreview(preset.id)}
                  disabled={soundDisabled || isPlaying || isLocked}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPlaying ? t('notificationSection.playing') : t('notificationSection.listen')}
                </button>
                <button
                  type="button"
                  onClick={() => handleCalendarSoundChange(preset.id)}
                  disabled={isCalendarSelected || isLocked}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${isCalendarSelected ? 'bg-blue-600 text-white' : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)]'}`}
                >
                  {t('notificationSection.calendar')}
                </button>
                <button
                  type="button"
                  onClick={() => handleBreakSoundChange(preset.id)}
                  disabled={isBreakSelected || isLocked}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${isBreakSelected ? 'bg-emerald-600 text-white' : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)]'}`}
                >
                  {t('notificationSection.breakLabel')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!isPlusUser && (
        <div className="flex items-start gap-3 mt-4 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10">
          <Crown className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">{t('notificationSection.plusSoundPackTitle')}</p>
            <p className="text-xs text-amber-600/90 dark:text-amber-400/90 mt-1">{t('notificationSection.plusSoundPackDescription')}</p>
          </div>
        </div>
      )}

      {soundDisabled && (
        <p className="mt-4 text-sm text-amber-600 dark:text-amber-400">
          {t('notificationSection.soundDisabledHint')}
        </p>
      )}
    </>
  );
};

export default NotificationSettingsSection;
